import { makeObservable, observable, action, computed, toJS } from "mobx";
import { Api } from "./Api";

/**
 * Configuration options for `PromiseStore`
 */
interface Options {
	debounce?: number; // Reserved for future debounce support
	cacheKey?: string; // If set, stores the response in a static cache
}

/**
 * AsyncStateStore: A generic MobX store for managing async operations.
 *
 * @template LoaderFn - The function that fetches data asynchronously.
 * @template Data - The type of data expected from `LoaderFn`.
 * @template Args - The parameters required by `LoaderFn`.
 * @template ErrorType - The type of error that may occur.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export class AsyncStateStore<
	LoaderFn extends (...args: any[]) => Promise<any>,
	Data = Awaited<ReturnType<LoaderFn>>,
	Args extends any[] = Parameters<LoaderFn>,
	ErrorType = Error, // Default error type is `Error`
> {
	/** 🔹 Global cache storage (not observable) */
	private static cache = new Map<string, any>();

	/** 🔹 Function responsible for fetching data */
	private readonly loader: LoaderFn | null;

	/** 🔹 Optional configuration settings */
	private readonly options: Options;

	/** 🔹 Custom error handler (optional) */
	private readonly errorHandler?: (error: ErrorType) => void;

	// ==============================================
	// 🔹 INTERNAL STATE (Private Properties)
	// ==============================================

	private _loading = false; // True when data is being fetched
	private _loaded = false; // True when data has been fetched at least once
	private _success = false; // True if the last fetch was successful
	private _data: Data | null = null; // Stores the retrieved data
	private _error: ErrorType | null = null; // Stores the last error
	private _cancelled = false; // True if fetch was cancelled
	private _requestId = 0;
	private _lastFetchArgs: Args | null = null; // Last args used by `fetch`, reused by `silentFetch`
	private debounceTimer: ReturnType<typeof setTimeout> | null = null;
	private abortController: AbortController | null = null;

	/**
	 * Constructs a new `PromiseStore` instance.
	 *
	 * @param loader - The async function used to fetch data.
	 * @param options - Optional settings (cache, debounce, etc.).
	 * @param errorHandler - Function that handles errors globally.
	 */
	constructor(
		loader: LoaderFn | null = null,
		options: Options | null = null,
		errorHandler?: (error: ErrorType) => void,
	) {
		this.loader = loader;
		this.options = options ?? {};
		this.errorHandler = errorHandler;

		// ✅ MobX observable setup
		makeObservable(this, {
			//@ts-expect-error Mobx typescript issue
			_loading: observable,
			_loaded: observable,
			_success: observable,
			_data: observable,
			_error: observable,
			_cancelled: observable,

			loading: computed,
			loaded: computed,
			success: computed,
			data: computed,
			error: computed,
			cancelled: computed,

			fetch: action,
			refetch: action,
			silentFetch: action,
			clear: action,
			cancel: action,
			updateData: action,
			fetchIfEmpty: action,
			_update: action,
		});
	}

	// ==============================================
	// 🔹 COMPUTED GETTERS (Read-Only)
	// ==============================================

	/** ✅ True if data is currently being fetched */
	public get loading() {
		return this._loading;
	}

	/** ✅ True if at least one successful fetch has occurred */
	public get loaded() {
		return this._loaded;
	}

	/** ✅ True if the last fetch was successful */
	public get success() {
		return this._success;
	}

	/** ✅ The most recently fetched data */
	public get data() {
		return toJS(this._data);
	}

	/** ✅ The last error encountered (or null if none) */
	public get error() {
		return toJS(this._error);
	}

	/** ✅ True if a fetch was cancelled */
	public get cancelled() {
		return this._cancelled;
	}

	// ==============================================
	// 🔹 ACTIONS (State-Changing Methods)
	// ==============================================

	/**
	 * 🚀 Clears all stored data and resets state.
	 */
	public clear() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = null;
		}

		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}

		this._requestId = 0;
		this._cancelled = false;

		this._update(false, false, false, null, null);
	}

	/**
	 * 🚀 Fetches data using the provided loader function.
	 *
	 * @param args - Arguments required by the loader function.
	 * @returns The fetched data of type `Promise<Data>`.
	 * @throws If the request fails, throws an error of type `ErrorType`.
	 */
	public async fetch(...args: Args): Promise<Data> {
		if (!this.loader) throw new Error("AsyncStateStore: Loader function not defined");

		this._lastFetchArgs = args;
		const debounceMs = this.options.debounce ?? 0;

		// 🔹 increment request id & reset cancelled state
		this._requestId = this._requestId + 1;
		const currentRequestId = this._requestId;
		this._cancelled = false;

		// 🔹 abort the previous HTTP request (if any)
		if (this.abortController) {
			this.abortController.abort();
		}
		this.abortController = new AbortController();
		const { signal } = this.abortController;

		const runFetch = async (): Promise<Data> => {
			// Cache
			if (this.options.cacheKey && AsyncStateStore.cache.has(this.options.cacheKey)) {
				const cachedData = AsyncStateStore.cache.get(this.options.cacheKey) as Data;
				this._update(false, true, true, cachedData, null);
				return cachedData;
			}

			// Stale-while-revalidate: only flip the loading flag (and clear the previous
			// error). Keep _data / _loaded / _success from the prior successful fetch
			// so consumers don't briefly see "no data" — e.g. list rows flickering to
			// empty during pagination, sidebar counts collapsing to 0 on filter toggles.
			this._loading = true;
			this._error = null;

			try {
				// 🔹 bind signal to all Api calls inside loader
				const responseData: Data = await Api.runWithSignal(signal, () => this.loader!(...args));

				// stale/cancelled protection
				if (this._cancelled || currentRequestId !== this._requestId) {
					return (this._data as Data) ?? responseData;
				}

				if (this.options.cacheKey) {
					AsyncStateStore.cache.set(this.options.cacheKey, responseData);
				}

				this._update(false, true, true, responseData, null);
				return responseData;
			} catch (error: unknown) {
				const typedError = error as ErrorType;

				// stale/cancelled -> ignore error and keep current data
				if (this._cancelled || currentRequestId !== this._requestId) {
					return this._data as Data;
				}

				this._update(false, true, false, null, typedError);

				if (this.errorHandler) {
					this.errorHandler(typedError);
				}

				throw typedError;
			}
		};

		if (debounceMs > 0) {
			// show spinner immediately
			this._loading = true;

			if (this.debounceTimer) {
				clearTimeout(this.debounceTimer);
			}

			return new Promise<Data>((resolve, reject) => {
				this.debounceTimer = setTimeout(() => {
					this.debounceTimer = null;
					runFetch().then(resolve).catch(reject);
				}, debounceMs);
			});
		}

		return runFetch();
	}

	/**
	 * 🚀 Re-runs `fetch` with the last args. Use after a mutation when you want
	 * the standard loading flow (`loading` flag flips on, debounce/cancellation apply).
	 */
	public async refetch(): Promise<Data> {
		if (!this._lastFetchArgs) {
			throw new Error("AsyncStateStore: refetch requires a prior successful fetch");
		}
		return this.fetch(...this._lastFetchArgs);
	}

	/**
	 * 🚀 Re-fetches data without flipping the `loading` flag, reusing the last
	 * args passed to `fetch`. Use after a side-effect that mutates a single row
	 * (e.g. toggling a switch) when you want the underlying list to refresh
	 * without showing a list-wide loading state. On success, `_data` is updated
	 * via `updateData`; on error, existing data is preserved and the error is rethrown.
	 */
	public async silentFetch(): Promise<Data> {
		if (!this.loader) throw new Error("AsyncStateStore: Loader function not defined");
		if (!this._lastFetchArgs) {
			throw new Error("AsyncStateStore: silentFetch requires a prior successful fetch");
		}

		this._requestId = this._requestId + 1;
		const currentRequestId = this._requestId;

		try {
			const responseData: Data = await this.loader(...this._lastFetchArgs);

			// stale/cancelled protection
			if (this._cancelled || currentRequestId !== this._requestId) {
				return (this._data as Data) ?? responseData;
			}

			this.updateData(responseData);
			return responseData;
		} catch (error) {
			if (this._cancelled || currentRequestId !== this._requestId) {
				return this._data as Data;
			}
			throw error;
		}
	}

	/**
	 * 🚀 Cancels the request.
	 */
	public cancel() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = null;
		}

		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}

		if (this._loading) {
			this._cancelled = true;
			this._requestId = this._requestId + 1;
			this._loading = false;
		}
	}

	public updateData(data: Data | null) {
		this._data = data;
		this._loaded = true;
		this._success = data !== null;
	}

	/**
	 * 🚀 Fetches data only if it hasn't been loaded successfully.
	 *
	 * @param args - Arguments required by the loader function.
	 * @returns The cached or newly fetched data as `Promise<Data>`.
	 */
	public async fetchIfEmpty(...args: Args): Promise<Data> {
		// ✅ If data has already been loaded successfully, return it
		if (this._loaded && this._success && this._data !== null) {
			return this._data;
		}

		// ✅ Otherwise, fetch new data
		return this.fetch(...args);
	}

	// ==============================================
	// 🔹 INTERNAL METHODS (Private)
	// ==============================================

	/**
	 * 🔹 Updates store state (internal use only).
	 *
	 * @param loading - Whether data is being fetched.
	 * @param loaded - Whether data has been fetched at least once.
	 * @param success - Whether the last fetch was successful.
	 * @param data - The fetched data.
	 * @param error - Any error encountered during fetch.
	 */
	private _update(loading: boolean, loaded: boolean, success: boolean, data: Data | null, error: ErrorType | null) {
		this._loading = loading;
		this._loaded = loaded;
		this._success = success;
		this._data = data;
		this._error = error;
	}

	// ==============================================
	// 🔹 STATIC METHODS (Cache Management)
	// ==============================================

	/**
	 * 🚀 Clears cached responses.
	 *
	 * @param cacheKey - (Optional) The specific cache key to clear.
	 */
	public static clearCache(cacheKey?: string) {
		if (cacheKey) {
			AsyncStateStore.cache.delete(cacheKey);
		} else {
			AsyncStateStore.cache.clear();
		}
	}

	/**
	 * 🚀 Gets a cached response by key.
	 */
	public static getCache<T>(cacheKey: string): T | undefined {
		return AsyncStateStore.cache.get(cacheKey) as T | undefined;
	}

	/**
	 * 🚀 Sets a cached response by key.
	 */
	public static setCache<T>(cacheKey: string, data: T): void {
		AsyncStateStore.cache.set(cacheKey, data);
	}
}
